import React from "react";
import { X, ShoppingCart, Trash2, Plus, Minus, Repeat } from "lucide-react";
import { Button } from "./ui/button";
import { useCart } from "../context/CartContext";

export default function CartDrawer() {
  const {
    items,
    open,
    closeCart,
    removeItem,
    updateQuantity,
    subtotal,
    hasSubscription,
    checkout,
    checkingOut,
    checkoutUrl,
  } = useCart();

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeCart}
        data-testid="cart-overlay"
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-[70] shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="cart-drawer"
        aria-hidden={!open}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-royal-blue text-white">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            <h2 className="font-heading text-lg font-bold">Your Cart</h2>
          </div>
          <button
            onClick={closeCart}
            className="p-1.5 rounded hover:bg-white/10 transition-colors"
            data-testid="cart-close-btn"
            aria-label="Close cart"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="text-center py-16" data-testid="cart-empty">
              <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Your cart is empty.</p>
            </div>
          ) : (
            <ul className="space-y-4" data-testid="cart-items">
              {items.map((item) => (
                <li
                  key={item.lineId}
                  className="flex gap-3 border-b border-slate-100 pb-4"
                  data-testid={`cart-item-${item.internal_id}`}
                >
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-14 h-14 rounded object-cover flex-shrink-0 bg-slate-100"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded bg-kingdom-gold/10 flex items-center justify-center flex-shrink-0">
                      {item.type === "subscription" ? (
                        <Repeat className="w-5 h-5 text-kingdom-gold" />
                      ) : (
                        <ShoppingCart className="w-5 h-5 text-kingdom-gold" />
                      )}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-royal-blue truncate">{item.name}</p>
                    {item.type === "subscription" && (
                      <span className="text-xs text-royal-purple font-medium">Monthly subscription</span>
                    )}
                    <p className="text-sm font-bold text-slate-700 mt-0.5">
                      ${Number(item.price).toFixed(2)}
                      {item.type === "subscription" && <span className="text-xs text-slate-400">/mo</span>}
                    </p>

                    <div className="flex items-center justify-between mt-2">
                      {item.type === "subscription" ? (
                        <span className="text-xs text-slate-400">Qty 1</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.lineId, item.quantity - 1)}
                            className="w-6 h-6 rounded border border-slate-200 flex items-center justify-center hover:bg-slate-100 disabled:opacity-40"
                            disabled={item.quantity <= 1}
                            data-testid={`cart-dec-${item.internal_id}`}
                            aria-label="Decrease quantity"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="text-sm w-5 text-center" data-testid={`cart-qty-${item.internal_id}`}>
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.lineId, item.quantity + 1)}
                            className="w-6 h-6 rounded border border-slate-200 flex items-center justify-center hover:bg-slate-100"
                            data-testid={`cart-inc-${item.internal_id}`}
                            aria-label="Increase quantity"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                      <button
                        onClick={() => removeItem(item.lineId)}
                        className="text-slate-400 hover:text-red-500 transition-colors"
                        data-testid={`cart-remove-${item.internal_id}`}
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-slate-200 px-5 py-4 space-y-3 bg-parchment">
            {hasSubscription && (
              <p className="text-xs text-slate-500">
                Includes a monthly subscription. Your card is billed monthly until canceled.
              </p>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Subtotal</span>
              <span className="font-bold text-lg text-royal-blue" data-testid="cart-subtotal">
                ${subtotal.toFixed(2)}
              </span>
            </div>
            <Button
              className="w-full btn-gold"
              onClick={checkout}
              disabled={checkingOut}
              data-testid="cart-checkout-btn"
            >
              {checkingOut ? "Redirecting to checkout..." : "Checkout Securely"}
            </Button>
            {checkoutUrl && (
              <a
                href={checkoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center text-sm font-semibold text-royal-blue underline"
                data-testid="cart-stripe-fallback-link"
              >
                Open secure Stripe checkout
              </a>
            )}
            <p className="text-[11px] text-center text-slate-400">
              Secure checkout. You can review everything before paying.
            </p>
          </div>
        )}
      </aside>
    </>
  );
}
